import {
  getAttendanceById,
  getAttendanceVersions,
  Version,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { TempAttendanceMonth } from "@/config/types";
import { formatDateFromLang } from "@/config/utils";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { attendanceToTempAttendance } from "../../attendanceUtils";

type UseAttendanceDesktopVersionsDrawerProps = {
  onClose: () => void;
  setTempAttendance: Dispatch<SetStateAction<TempAttendanceMonth | undefined>>;
};

export function useAttendanceDesktopVersionsDrawer({
  onClose,
  setTempAttendance,
}: UseAttendanceDesktopVersionsDrawerProps) {
  const { lang, selectedMandate, selectedMandateId } = useContext(PspContext);
  const {
    selectedMonth,
    selectedYear,
    setAttendanceMonth,
    attendanceVersions,
    setAttendanceVersions,
    setIsSubmitted,
    setSubmittedAt,
  } = useContext(AttendancesContext);

  const [restoreConfirmationOpenVersion, setRestoreConfirmationOpenVersion] =
    useState<Version | null>(null);
  const [restoreConfirmationOpen, setRestoreConfirmationOpen] = useState(false);

  useEffect(() => {
    if (!selectedMandate) {
      if (!selectedMandateId) {
        toast(translate("noMandateSelected", lang));
      }
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    getAttendanceVersions({
      query: {
        mandate_id: selectedMandate.id,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAttendanceVersions(data);
    });
  }, [
    lang,
    selectedMandate,
    selectedMandateId,
    selectedMonth,
    selectedYear,
    setAttendanceVersions,
  ]);

  function closeRestoreVersionConfirmation() {
    setRestoreConfirmationOpen(false);
    setTimeout(() => setRestoreConfirmationOpenVersion(null), 250);
  }

  function onRestoreVersion() {
    if (!restoreConfirmationOpenVersion) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    if (!selectedMandate) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }

    getAttendanceById({
      path: { attendance_id: restoreConfirmationOpenVersion.id },
      query: {
        mandate_id: selectedMandate.id,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ error, data }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAttendanceMonth(data.data);
      setTempAttendance(attendanceToTempAttendance(data.data));
      setIsSubmitted(data.isSubmitted);
      setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
      toast(
        translate("versionRestoredSuccessfully", lang, {
          "{date}": formatDateFromLang(
            new Date(restoreConfirmationOpenVersion.timestamp),
            lang,
          ),
        }),
      );
      closeRestoreVersionConfirmation();
      onClose();
    });
  }

  function openRestoreVersionConfirmation(version: Version) {
    setRestoreConfirmationOpen(true);
    setRestoreConfirmationOpenVersion(version);
  }

  return {
    lang,
    attendanceVersions,
    setAttendanceMonth,
    closeRestoreVersionConfirmation,
    openRestoreVersionConfirmation,
    restoreConfirmationOpen,
    restoreConfirmationOpenVersion,
    onRestoreVersion,
  };
}
